import React, { useState, useEffect, useCallback, useRef } from 'react';
import { gsap } from 'gsap';
import styles from './FirstScreen.module.css';

import moran1 from "../images/מורן וינברגר פרוייקטים 10.webp";
import moran2 from "../images/מורן וינברגר פרוייקטים 9.webp";
import moran3 from "../images/מורן וינברגר פרוייקטים 11.webp";
import logo from "../images/מורן וינברגר לוגו.webp";

const heroSlides = [
  { id: 1, image: moran1 },
  { id: 2, image: moran2 },
  { id: 3, image: moran3 },
];

const MoranHero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const autoplayRef = useRef(null);
  const progressRef = useRef(null);
  const heroRef = useRef(null);

  const SLIDE_DURATION = 6000;

  const animateProgress = useCallback(() => {
    if (progressRef.current) {
      gsap.fromTo(progressRef.current,
        { scaleX: 0 },
        { scaleX: 1, duration: SLIDE_DURATION / 1000, ease: 'none' }
      );
    }
  }, []);

  const goToSlide = useCallback((index) => {
    if (isTransitioning || index === currentSlide) return;
    setIsTransitioning(true);
    setCurrentSlide(index);
    setTimeout(() => setIsTransitioning(false), 1000);
  }, [currentSlide, isTransitioning]);

  const nextSlide = useCallback(() => {
    const next = currentSlide === heroSlides.length - 1 ? 0 : currentSlide + 1;
    goToSlide(next);
  }, [currentSlide, goToSlide]);

  const prevSlide = useCallback(() => {
    const prev = currentSlide === 0 ? heroSlides.length - 1 : currentSlide - 1;
    goToSlide(prev);
  }, [currentSlide, goToSlide]);

  useEffect(() => {
    autoplayRef.current = setInterval(() => nextSlide(), SLIDE_DURATION);
    return () => { if (autoplayRef.current) clearInterval(autoplayRef.current); };
  }, [nextSlide]);

  useEffect(() => {
    animateProgress();
  }, [currentSlide, animateProgress]);

  // Entrance animation
  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ delay: 0.15 });

      tl.fromTo('[data-anim="frame"]',
        { opacity: 0 },
        { opacity: 1, duration: 0.4, stagger: 0.04, ease: 'power2.out', clearProps: 'opacity' }
      )
      .fromTo('[data-anim="logo"]',
        { opacity: 0, y: -15 },
        { opacity: 1, y: 0, duration: 0.45, ease: 'power3.out', clearProps: 'all' },
        '-=0.2'
      )
      .fromTo('[data-anim="title"]',
        { opacity: 0, y: 25 },
        { opacity: 1, y: 0, duration: 0.55, ease: 'power3.out', clearProps: 'all' },
        '-=0.25'
      )
      .fromTo('[data-anim="subtitle"]',
        { opacity: 0, y: 10 },
        { opacity: 1, y: 0, duration: 0.4, ease: 'power2.out', clearProps: 'all' },
        '-=0.25'
      )
      .fromTo('[data-anim="cta"]',
        { opacity: 0, y: 8 },
        { opacity: 1, y: 0, duration: 0.35, stagger: 0.06, ease: 'power2.out', clearProps: 'all' },
        '-=0.2'
      )
      .fromTo('[data-anim="bottom"]',
        { opacity: 0, y: 6 },
        { opacity: 1, y: 0, duration: 0.3, ease: 'power2.out', clearProps: 'all' },
        '-=0.15'
      )
      .fromTo('[data-anim="scroll"]',
        { opacity: 0 },
        { opacity: 1, duration: 0.3, ease: 'power2.out', clearProps: 'opacity' },
        '-=0.2'
      );
    }, heroRef);

    return () => ctx.revert();
  }, []);

  const handleManualNav = (action) => {
    if (autoplayRef.current) clearInterval(autoplayRef.current);
    action();
    autoplayRef.current = setInterval(() => nextSlide(), SLIDE_DURATION);
  };

  return (
    <section className={styles.heroSection} ref={heroRef}>
      {/* Grain texture */}
      <div className={styles.grainOverlay} />

      {/* Background Images */}
      <div className={styles.slidesContainer}>
        {heroSlides.map((slide, index) => (
          <div
            key={slide.id}
            className={`${styles.slide} ${index === currentSlide ? styles.slideActive : ''}`}
          >
            <div className={styles.imageWrapper}>
              <img
                src={slide.image}
                alt={`עיצוב פנים בעבודתה של מורן וינברגר - תמונה ${index + 1}`}
                className={styles.slideImage}
                fetchPriority={index === 0 ? 'high' : 'low'}
                decoding="async"
                loading="eager"
              />
            </div>
          </div>
        ))}
        <div className={styles.overlay} />
      </div>

      {/* Decorative Frame */}
      <div className={`${styles.frameCorner} ${styles.frameTR}`} data-anim="frame" />
      <div className={`${styles.frameCorner} ${styles.frameTL}`} data-anim="frame" />
      <div className={`${styles.frameCorner} ${styles.frameBR}`} data-anim="frame" />
      <div className={`${styles.frameCorner} ${styles.frameBL}`} data-anim="frame" />

      {/* Logo at top */}
      <div className={styles.logoArea} data-anim="logo">
        <img src={logo} alt="מורן וינברגר - עיצוב פנים וניהול פרויקטים" className={styles.heroLogo} />
        <span className={styles.brandName}>Moran Weinberger</span>
      </div>

      {/* Content */}
      <div className={styles.heroContent}>
        <div className={styles.contentInner}>
          {/* Title */}
          <h1 className={styles.heroTitle} data-anim="title">
            אתם מביאים את החזון
            <br />
            <span className={styles.highlight}>אני דואגת לכל השאר</span>
          </h1>

          {/* Subtitle */}
          <p className={styles.heroSubtitle} data-anim="subtitle">
            עיצוב פנים וניהול פרויקטים מקצועי - תכנון מדויק, תקציב שקוף, ולוחות זמנים שעובדים. בלי הפתעות ובלי כאבי ראש
          </p>

          {/* CTAs */}
          <div className={styles.ctaContainer}>
            <a href="#פרוייקטים" className={styles.ctaPrimary} data-anim="cta">
              <span className={styles.ctaText}>הפרויקטים שלי</span>
            </a>
            <a href="https://wa.me/972502177062?text=%D7%94%D7%99%D7%99%20%D7%9E%D7%95%D7%A8%D7%9F%2C%20%D7%94%D7%92%D7%A2%D7%AA%D7%99%20%D7%9E%D7%94%D7%90%D7%AA%D7%A8%20%D7%A9%D7%9C%D7%9A%2C%20%D7%90%D7%A9%D7%9E%D7%97%20%D7%9C%D7%A9%D7%9E%D7%95%D7%A2%20%D7%A2%D7%95%D7%93%20%D7%A2%D7%9C.." target="_blank" rel="noopener noreferrer" className={styles.ctaOutline} data-anim="cta">
              <span className={styles.ctaText}>בואו נדבר</span>
            </a>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className={styles.bottomBar} data-anim="bottom">
        <div className={styles.navArrows}>
          <button
            className={styles.navArrow}
            onClick={() => handleManualNav(prevSlide)}
            aria-label="הקודם"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
          <button
            className={styles.navArrow}
            onClick={() => handleManualNav(nextSlide)}
            aria-label="הבא"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className={styles.scrollIndicator} data-anim="scroll">
        <span className={styles.scrollText}>גלול למטה</span>
        <div className={styles.scrollLine}>
          <div className={styles.scrollDot} />
        </div>
      </div>
    </section>
  );
};

export default MoranHero;
